import * as React from 'react'

// --- Hooks ---
import { useTiptapEditor } from '@tiptap-ui/hooks/use-tiptap-editor'

// --- Lib ---
import { isMarkInSchema, isNodeTypeSelected } from '@tiptap-ui/lib/tiptap-utils'

// --- Icons ---
import { BoldIcon } from '@tiptap-ui/components/tiptap-icons/bold-icon'
import { Code2Icon } from '@tiptap-ui/components/tiptap-icons/code2-icon'
import { ItalicIcon } from '@tiptap-ui/components/tiptap-icons/italic-icon'
import { StrikeIcon } from '@tiptap-ui/components/tiptap-icons/strike-icon'
import { SubscriptIcon } from '@tiptap-ui/components/tiptap-icons/subscript-icon'
import { SuperscriptIcon } from '@tiptap-ui/components/tiptap-icons/superscript-icon'
import { UnderlineIcon } from '@tiptap-ui/components/tiptap-icons/underline-icon'

export const markIcons = {
  bold: BoldIcon,
  strong: BoldIcon,
  italic: ItalicIcon,
  em: ItalicIcon,
  underline: UnderlineIcon,
  strike: StrikeIcon,
  code: Code2Icon,
  superscript: SuperscriptIcon,
  subscript: SubscriptIcon,
  subsup_sup: SuperscriptIcon,
  subsup_sub: SubscriptIcon,
}

export const MARK_SHORTCUT_KEYS = {
  bold: 'mod+b',
  strong: 'mod+b',
  italic: 'mod+i',
  em: 'mod+i',
  underline: 'mod+u',
  strike: 'mod+shift+s',
  code: 'mod+e',
  superscript: 'mod+.',
  subscript: 'mod+,',
  subsup_sup: 'mod+.',
  subsup_sub: 'mod+,',
}

/**
 * Checks if a mark can be toggled in the current editor state
 */
export function canToggleMark (editor, type, attrs) {
  if (!editor || !editor.isEditable) return false
  if (!isMarkInSchema(type, editor) || isNodeTypeSelected(editor, ['image'])) { return false }

  return editor.can().toggleMark(type, attrs)
}

/**
 * Checks if a mark is currently active
 */
export function isMarkActive (editor, type, attrs) {
  if (!editor || !editor.isEditable) return false
  return editor.isActive(type, attrs)
}

/**
 * Toggles a mark in the editor
 */
export function toggleMark (editor, type, attrs) {
  if (!editor || !editor.isEditable) return false
  if (!canToggleMark(editor, type, attrs)) return false

  return editor.chain().focus().toggleMark(type, attrs).run()
}

/**
 * Determines if the mark button should be shown
 */
export function shouldShowButton (props) {
  const { editor, type, hideWhenUnavailable, attrs } = props

  if (!editor || !editor.isEditable) return false
  if (!isMarkInSchema(type, editor)) return false

  if (hideWhenUnavailable && !editor.isActive('code')) {
    return canToggleMark(editor, type, attrs)
  }

  return true
}

/**
 * Gets the formatted mark name
 */
export function getFormattedMarkName (type) {
  return type.charAt(0).toUpperCase() + type.slice(1)
}

/**
 * Custom hook that provides mark functionality for Tiptap editor
 *
 * @example
 * ```tsx
 * // Simple usage
 * function MySimpleBoldButton() {
 *   const { isVisible, handleMark } = useMark({ type: "bold" })
 *
 *   if (!isVisible) return null
 *
 *   return <button onClick={handleMark}>Bold</button>
 * }
 *
 * // Advanced usage with configuration
 * function MyAdvancedItalicButton() {
 *   const { isVisible, handleMark, label, isActive } = useMark({
 *     editor: myEditor,
 *     type: "italic",
 *     hideWhenUnavailable: true,
 *     onToggled: () => console.log('Mark toggled!')
 *   })
 *
 *   if (!isVisible) return null
 *
 *   return (
 *     <MyButton
 *       onClick={handleMark}
 *       aria-pressed={isActive}
 *       aria-label={label}
 *     >
 *       Italic
 *     </MyButton>
 *   )
 * }
 * ```
 */
export function useMark (config) {
  const {
    editor: providedEditor,
    type,
    attrs,
    hideWhenUnavailable = false,
    onToggled,
  } = config
  const { editor } = useTiptapEditor(providedEditor)
  const [isVisible, setIsVisible] = React.useState(true)
  const canToggle = canToggleMark(editor, type, attrs)
  const isActive = isMarkActive(editor, type, attrs)
  const typeKey = attrs?.type ? `${type}_${attrs.type}` : type

  React.useEffect(() => {
    if (!editor) return

    const handleSelectionUpdate = () => {
      setIsVisible(shouldShowButton({ editor, type, hideWhenUnavailable }))
    }

    handleSelectionUpdate()

    editor.on('selectionUpdate', handleSelectionUpdate)

    return () => {
      editor.off('selectionUpdate', handleSelectionUpdate)
    }
  }, [editor, type, hideWhenUnavailable])

  const handleMark = React.useCallback(() => {
    if (!editor) return false

    const success = toggleMark(editor, type, attrs)
    if (success) {
      onToggled?.()
    }
    return success
  }, [editor, type, attrs, onToggled])

  return {
    isVisible,
    isActive,
    handleMark,
    canToggle,
    label: getFormattedMarkName(type),
    shortcutKeys: MARK_SHORTCUT_KEYS[typeKey],
    Icon: markIcons[typeKey],
  }
}
